import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EventIcon from "@mui/icons-material/Event";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import GroupsIcon from "@mui/icons-material/Groups";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ScheduleIcon from "@mui/icons-material/Schedule";
import StadiumIcon from "@mui/icons-material/Stadium";
import { Alert, Box, Button, Chip, CircularProgress, Divider, IconButton, Paper, Stack, Tooltip, Typography, } from "@mui/material";
import { addDays, addMonths, eachDayOfInterval, endOfDay, endOfMonth, endOfWeek, format, getDay, isBefore, isSameDay, isSameMonth, isWithinInterval, startOfDay, startOfMonth, startOfWeek, subMonths, } from "date-fns";
import React from "react";
// Day name abbreviations (Sunday = 0, Monday = 1, etc.)
const ALL_DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
// Get day names in order based on week start day
const getDayNames = (weekStartDay) => {
    const names = [];
    for (let i = 0; i < 7; i++) {
        names.push(ALL_DAY_NAMES[(weekStartDay + i) % 7]);
    }
    return names;
};
/**
 * Ensure a value is a Date object. Handles various date formats from form state.
 */
const ensureDate = (value) => {
    if (!value)
        return null;
    if (value instanceof Date)
        return value;
    if (typeof value === "string") {
        // Handle ISO datetime strings (e.g., "2026-02-16T00:00:00.000Z")
        // or simple date strings (e.g., "2026-02-16")
        const dateStr = value.split("T")[0]; // Get just the date part
        const parts = dateStr?.split("-").map(Number);
        if (isNaN(parts[0]) || isNaN(parts[1]) || isNaN(parts[2]))
            return null;
        return new Date(parts[0], parts[1] - 1, parts[2], 12, 0, 0);
    }
    return null;
};
export const FinalizeStep = ({ teamCount, divisionCount, formatRounds, venues, matchTiming, scheduleWindow, exclusions, onGenerate, isGenerating, generateError, }) => {
    // Normalize exclusion dates to ensure they're Date objects (form state may serialize them as strings)
    const excludedDates = React.useMemo(() => (exclusions?.excludedDates || [])
        .map((ed) => ({
        ...ed,
        date: ensureDate(ed.date),
    }))
        .filter((ed) => ed.date !== null), [exclusions?.excludedDates]);
    // Normalize blackout periods - ensure full day coverage (start of day to end of day)
    const blackoutPeriods = React.useMemo(() => (exclusions?.blackoutPeriods || [])
        .map((bp) => {
        const start = ensureDate(bp.startDate);
        const end = ensureDate(bp.endDate);
        if (!start || !end)
            return null;
        return {
            ...bp,
            startDate: startOfDay(start),
            endDate: endOfDay(end),
        };
    })
        .filter((bp) => bp !== null), [exclusions?.blackoutPeriods]);
    const gameWeekStartDay = formatRounds?.gameWeekStartDay ?? 0;
    const [currentMonth, setCurrentMonth] = React.useState(formatRounds?.startDate || new Date());
    // Calculate season stats
    const gamesPerRound = Math.floor(teamCount / 2);
    const scheduleMode = formatRounds?.scheduleMode || "roundRobin";
    // For round robin: calculate based on matchesPerPairing
    const roundRobinTotalRounds = (formatRounds?.matchesPerPairing || 1) *
        (teamCount > 0 ? teamCount - 1 : 0);
    const roundRobinTotalGames = gamesPerRound * roundRobinTotalRounds;
    const startDate = formatRounds?.startDate;
    const endDate = formatRounds?.endDate;
    // Use timestamps for dependency tracking (Date objects don't compare correctly)
    const startDateTs = startDate?.getTime();
    const endDateTs = endDate?.getTime();
    // Match duration display
    const matchDuration = matchTiming?.matchDuration || 0;
    const warmupTime = matchTiming?.warmupTime || 0;
    const totalSlotTime = matchDuration + warmupTime;
    // Venue mode display
    const venueMode = venues?.venueMode === "homeAway"
        ? "Home & Away"
        : venues?.venueMode === "neutral"
            ? "Neutral Venues"
            : "Not set";
    const pitchCount = venues?.selectedPitchIds?.length || 0;
    // Get enabled days from pitch schedules (0=Sun, 1=Mon, ..., 6=Sat)
    const enabledDays = React.useMemo(() => {
        const enabled = new Set();
        scheduleWindow?.pitchSchedules?.forEach((ps) => {
            Object.entries(ps.days).forEach(([key, config]) => {
                if (config.enabled) {
                    const dayMap = {
                        sunday: 0,
                        monday: 1,
                        tuesday: 2,
                        wednesday: 3,
                        thursday: 4,
                        friday: 5,
                        saturday: 6,
                    };
                    enabled.add(dayMap[key] ?? 0);
                }
            });
        });
        return enabled;
    }, [scheduleWindow?.pitchSchedules]);
    // Check if a date is excluded (single date)
    const isDateExcluded = (date) => {
        return excludedDates.find((ed) => isSameDay(ed.date, date));
    };
    // Check if a date falls in a whole-week exclusion
    const isInWholeWeekExclusion = (date) => {
        return excludedDates.find((ed) => {
            if (!ed.wholeWeek)
                return false;
            const weekStartDate = startOfWeek(ed.date, {
                weekStartsOn: gameWeekStartDay,
            });
            const weekEndDate = endOfWeek(ed.date, {
                weekStartsOn: gameWeekStartDay,
            });
            return isWithinInterval(date, { start: weekStartDate, end: weekEndDate });
        });
    };
    // Check if a date is in a blackout period
    const isInBlackout = (date) => {
        return blackoutPeriods.find((bp) => isWithinInterval(date, { start: bp.startDate, end: bp.endDate }));
    };
    // Helper: Check if ANY day in a week has an exclusion or blackout
    // If so, the entire game week is excluded from scheduling
    const isWeekExcluded = (weekStart, weekEnd) => {
        // Check if any whole-week exclusion matches this week
        const hasWholeWeekExclusion = excludedDates.some((ed) => {
            if (!ed.wholeWeek)
                return false;
            const edWeekStart = startOfWeek(ed.date, {
                weekStartsOn: gameWeekStartDay,
            });
            return isSameDay(edWeekStart, weekStart);
        });
        if (hasWholeWeekExclusion)
            return true;
        // Check if ANY day in this week overlaps with a blackout period
        const daysInWeek = eachDayOfInterval({ start: weekStart, end: weekEnd });
        return daysInWeek.some((day) => blackoutPeriods.some((bp) => isWithinInterval(day, { start: bp.startDate, end: bp.endDate })));
    };
    // Calculate number of playoff rounds needed
    const playoffRoundsNeeded = React.useMemo(() => {
        if (!formatRounds?.divisionPlayoffs)
            return 0;
        const enabledPlayoffs = formatRounds.divisionPlayoffs.filter((p) => p.enabled);
        if (enabledPlayoffs.length === 0)
            return 0;
        return Math.max(...enabledPlayoffs.map((p) => Math.ceil(Math.log2(p.teamCount))));
    }, [formatRounds?.divisionPlayoffs]);
    // Calculate all available weeks between start and end (for fillDates mode)
    const availableWeeks = React.useMemo(() => {
        if (!startDate)
            return [];
        if (scheduleMode === "fillDates" && !endDate)
            return [];
        const weeks = [];
        // GW1 should be the first FULL week starting ON or AFTER the start date
        let currentWeekStart = startOfWeek(startDate, {
            weekStartsOn: gameWeekStartDay,
        });
        // If start date is after the week start, move to the next week
        if (isBefore(currentWeekStart, startDate)) {
            currentWeekStart = addDays(currentWeekStart, 7);
        }
        const effectiveEndDate = scheduleMode === "fillDates" ? endDate : addDays(startDate, 365);
        const maxWeeks = 100;
        let weekCount = 0;
        while (currentWeekStart <= effectiveEndDate && weekCount < maxWeeks) {
            const weekEnd = endOfWeek(currentWeekStart, {
                weekStartsOn: gameWeekStartDay,
            });
            if (!isWeekExcluded(currentWeekStart, weekEnd)) {
                weeks.push({ weekStart: currentWeekStart, weekEnd });
            }
            currentWeekStart = addDays(currentWeekStart, 7);
            weekCount++;
        }
        return weeks;
    }, [
        startDate,
        endDate,
        scheduleMode,
        excludedDates,
        blackoutPeriods,
        startDateTs,
        endDateTs,
        gameWeekStartDay,
    ]);
    // Calculate gameweeks and playoffWeeks together
    const { gameWeeks, playoffWeeks, totalGames } = React.useMemo(() => {
        if (!startDate)
            return { gameWeeks: [], playoffWeeks: [], totalGames: 0 };
        // Calculate slots per week
        let slotsPerWeek = 0;
        scheduleWindow?.pitchSchedules?.forEach((ps) => {
            Object.values(ps.days).forEach((day) => {
                if (!day.enabled || !day.timeWindows[0])
                    return;
                const tw = day.timeWindows[0];
                const startParts = tw.startTime.split(":").map(Number);
                const endParts = tw.endTime.split(":").map(Number);
                const minutes = (endParts[0] ?? 0) * 60 +
                    (endParts[1] ?? 0) -
                    ((startParts[0] ?? 0) * 60 + (startParts[1] ?? 0));
                const slotDuration = matchTiming
                    ? matchTiming.matchDuration +
                        matchTiming.halfTimeBreak +
                        matchTiming.warmupTime
                    : 45;
                slotsPerWeek += Math.floor(minutes / slotDuration);
            });
        });
        if (slotsPerWeek === 0)
            return { gameWeeks: [], playoffWeeks: [], totalGames: 0 };
        const enabledPlayoffs = formatRounds?.divisionPlayoffs?.filter((p) => p.enabled) || [];
        if (scheduleMode === "roundRobin") {
            // ROUND ROBIN MODE
            const weeks = [];
            // GW1 should be the first FULL week starting ON or AFTER the start date
            let currentWeekStart = startOfWeek(startDate, {
                weekStartsOn: gameWeekStartDay,
            });
            // If start date is after the week start, move to the next week
            if (isBefore(currentWeekStart, startDate)) {
                currentWeekStart = addDays(currentWeekStart, 7);
            }
            let gamesScheduled = 0;
            let gameWeekNumber = 1;
            const maxWeeks = 100;
            let weekCount = 0;
            while (gamesScheduled < roundRobinTotalGames && weekCount < maxWeeks) {
                const weekEnd = endOfWeek(currentWeekStart, {
                    weekStartsOn: gameWeekStartDay,
                });
                if (!isWeekExcluded(currentWeekStart, weekEnd)) {
                    const gamesThisWeek = Math.min(gamesPerRound, roundRobinTotalGames - gamesScheduled);
                    weeks.push({
                        weekNumber: gameWeekNumber,
                        weekStart: currentWeekStart,
                        weekEnd,
                        gamesCount: gamesThisWeek,
                    });
                    gamesScheduled += gamesThisWeek;
                    gameWeekNumber++;
                }
                currentWeekStart = addDays(currentWeekStart, 7);
                weekCount++;
            }
            // Calculate playoff weeks after regular season
            const playoffs = [];
            if (enabledPlayoffs.length > 0 && weeks.length > 0) {
                const lastGameWeek = weeks[weeks.length - 1];
                currentWeekStart = addDays(lastGameWeek.weekEnd, 1);
                currentWeekStart = startOfWeek(currentWeekStart, {
                    weekStartsOn: gameWeekStartDay,
                });
                if (isWithinInterval(currentWeekStart, {
                    start: lastGameWeek.weekStart,
                    end: lastGameWeek.weekEnd,
                })) {
                    currentWeekStart = addDays(currentWeekStart, 7);
                }
                let playoffRound = 1;
                weekCount = 0;
                while (playoffRound <= playoffRoundsNeeded && weekCount < 20) {
                    const weekEnd = endOfWeek(currentWeekStart, {
                        weekStartsOn: gameWeekStartDay,
                    });
                    if (!isWeekExcluded(currentWeekStart, weekEnd)) {
                        let matchCount = 0;
                        enabledPlayoffs.forEach((p) => {
                            const divRounds = Math.ceil(Math.log2(p.teamCount));
                            const roundFromEnd = divRounds - playoffRound + 1;
                            if (roundFromEnd >= 1) {
                                matchCount += Math.pow(2, roundFromEnd - 1);
                            }
                        });
                        if (matchCount > 0) {
                            playoffs.push({
                                roundNumber: playoffRound,
                                weekStart: currentWeekStart,
                                weekEnd,
                                matchCount,
                            });
                        }
                        playoffRound++;
                    }
                    currentWeekStart = addDays(currentWeekStart, 7);
                    weekCount++;
                }
            }
            return {
                gameWeeks: weeks,
                playoffWeeks: playoffs,
                totalGames: roundRobinTotalGames,
            };
        }
        else {
            // FILL DATES MODE: Playoffs take priority at the end
            if (availableWeeks.length === 0) {
                return { gameWeeks: [], playoffWeeks: [], totalGames: 0 };
            }
            const playoffs = [];
            const playoffWeeksNeeded = playoffRoundsNeeded;
            let availableForLeague = [...availableWeeks];
            if (enabledPlayoffs.length > 0 && playoffWeeksNeeded > 0) {
                const playoffWeekSlots = [];
                let weeksReserved = 0;
                for (let i = availableWeeks.length - 1; i >= 0 && weeksReserved < playoffWeeksNeeded; i--) {
                    const week = availableWeeks[i];
                    if (week) {
                        playoffWeekSlots.unshift(week);
                        weeksReserved++;
                    }
                }
                availableForLeague = availableWeeks.slice(0, availableWeeks.length - weeksReserved);
                playoffWeekSlots.forEach((slot, index) => {
                    const playoffRound = index + 1;
                    let matchCount = 0;
                    enabledPlayoffs.forEach((p) => {
                        const divRounds = Math.ceil(Math.log2(p.teamCount));
                        const roundFromEnd = divRounds - playoffRound + 1;
                        if (roundFromEnd >= 1) {
                            matchCount += Math.pow(2, roundFromEnd - 1);
                        }
                    });
                    playoffs.push({
                        roundNumber: playoffRound,
                        weekStart: slot.weekStart,
                        weekEnd: slot.weekEnd,
                        matchCount,
                    });
                });
            }
            const weeks = availableForLeague.map((slot, index) => ({
                weekNumber: index + 1,
                weekStart: slot.weekStart,
                weekEnd: slot.weekEnd,
                gamesCount: gamesPerRound,
            }));
            const fillDatesTotalGames = weeks.length * gamesPerRound;
            return {
                gameWeeks: weeks,
                playoffWeeks: playoffs,
                totalGames: fillDatesTotalGames,
            };
        }
    }, [
        startDate,
        endDate,
        scheduleMode,
        scheduleWindow,
        matchTiming,
        roundRobinTotalGames,
        roundRobinTotalRounds,
        gamesPerRound,
        excludedDates,
        blackoutPeriods,
        availableWeeks,
        playoffRoundsNeeded,
        formatRounds?.divisionPlayoffs,
        startDateTs,
        endDateTs,
    ]);
    // Get gameweek for a date
    const getGameWeekForDate = (date) => {
        return gameWeeks.find((gw) => isWithinInterval(date, { start: gw.weekStart, end: gw.weekEnd }));
    };
    // Get playoff week for a date
    const getPlayoffWeekForDate = (date) => {
        return playoffWeeks.find((pw) => isWithinInterval(date, { start: pw.weekStart, end: pw.weekEnd }));
    };
    // Calendar navigation
    const goToPrevMonth = () => setCurrentMonth(subMonths(currentMonth, 1));
    const goToNextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));
    // Generate calendar days for current month
    const calendarDays = React.useMemo(() => {
        const monthStart = startOfMonth(currentMonth);
        const monthEnd = endOfMonth(currentMonth);
        const calStart = startOfWeek(monthStart, {
            weekStartsOn: gameWeekStartDay,
        });
        const calEnd = endOfWeek(monthEnd, { weekStartsOn: gameWeekStartDay });
        return eachDayOfInterval({ start: calStart, end: calEnd });
    }, [currentMonth, gameWeekStartDay]);
    // Estimated end date
    const estimatedEndDate = React.useMemo(() => {
        if (playoffWeeks.length > 0) {
            return playoffWeeks[playoffWeeks.length - 1]?.weekEnd ?? null;
        }
        if (gameWeeks.length > 0) {
            return gameWeeks[gameWeeks.length - 1]?.weekEnd ?? null;
        }
        return null;
    }, [gameWeeks, playoffWeeks]);
    // Total playoff matches
    const totalPlayoffMatches = React.useMemo(() => {
        if (!formatRounds?.divisionPlayoffs)
            return 0;
        return formatRounds.divisionPlayoffs
            .filter((p) => p.enabled)
            .reduce((sum, p) => sum + (p.teamCount - 1), 0);
    }, [formatRounds?.divisionPlayoffs]);
    // Validation
    const hasSchedule = scheduleWindow?.pitchSchedules?.some((ps) => Object.values(ps.days).some((d) => d.enabled));
    const isReady = scheduleMode === "manual"
        ? teamCount >= 2 && divisionCount >= 1
        : teamCount >= 2 &&
            divisionCount >= 1 &&
            formatRounds?.matchesPerPairing &&
            formatRounds?.startDate &&
            matchTiming?.matchDuration &&
            hasSchedule &&
            gameWeeks.length > 0;
    // Get playoff round name
    const getPlayoffRoundName = (roundNum, totalRounds) => {
        const roundsFromEnd = totalRounds - roundNum + 1;
        if (roundsFromEnd === 1)
            return "Final";
        if (roundsFromEnd === 2)
            return "Semi";
        if (roundsFromEnd === 3)
            return "QF";
        return `R${roundNum}`;
    };
    return (_jsxs(Box, { children: [_jsxs(Box, { sx: { textAlign: "center", mb: 3 }, children: [_jsx(Typography, { variant: "h5", fontWeight: 600, gutterBottom: true, children: scheduleMode === "manual"
                            ? "Activate Season"
                            : "Finalize & Generate" }), _jsx(Typography, { variant: "body2", color: "text.secondary", children: scheduleMode === "manual"
                            ? "Review your configuration and activate the season"
                            : "Review your configuration and generate the match schedule" })] }), scheduleMode === "manual" ? (_jsxs(_Fragment, { children: [_jsxs(Paper, { variant: "outlined", sx: { p: 3, mb: 3 }, children: [_jsxs(Stack, { direction: "row", spacing: 1.5, alignItems: "center", sx: { mb: 2 }, children: [_jsx(GroupsIcon, { color: "primary", fontSize: "small" }), _jsx(Typography, { variant: "subtitle2", fontWeight: 600, children: "Season Overview" })] }), _jsxs(Typography, { variant: "body2", color: "text.secondary", children: [teamCount, " teams in ", divisionCount, " division", divisionCount !== 1 ? "s" : ""] }), _jsx(Typography, { variant: "body2", color: "text.secondary", children: "Manual scheduling \u2014 matches will be created individually" })] }), _jsx(Alert, { severity: "info", sx: { mb: 3 }, children: _jsx(Typography, { variant: "body2", children: "The season will be activated with no pre-generated matches. You can create matches individually from the season page." }) })] })) : (_jsxs(_Fragment, { children: [_jsxs(Stack, { direction: { xs: "column", md: "row" }, spacing: 2, sx: { mb: 3 }, children: [_jsxs(Paper, { variant: "outlined", sx: { p: 2, flex: 1 }, children: [_jsxs(Stack, { direction: "row", spacing: 1.5, alignItems: "center", sx: { mb: 1 }, children: [_jsx(GroupsIcon, { color: "primary", fontSize: "small" }), _jsx(Typography, { variant: "subtitle2", fontWeight: 600, children: "Teams & Format" })] }), _jsxs(Typography, { variant: "body2", color: "text.secondary", children: [teamCount, " teams in ", divisionCount, " division", divisionCount !== 1 ? "s" : ""] }), _jsx(Typography, { variant: "body2", color: "text.secondary", children: scheduleMode === "fillDates"
                                            ? "Fill dates mode"
                                            : formatRounds?.matchesPerPairing === 1
                                                ? "Single round-robin"
                                                : `${formatRounds?.matchesPerPairing}x round-robin` })] }), _jsxs(Paper, { variant: "outlined", sx: { p: 2, flex: 1 }, children: [_jsxs(Stack, { direction: "row", spacing: 1.5, alignItems: "center", sx: { mb: 1 }, children: [_jsx(StadiumIcon, { color: "primary", fontSize: "small" }), _jsx(Typography, { variant: "subtitle2", fontWeight: 600, children: "Venues" })] }), _jsx(Typography, { variant: "body2", color: "text.secondary", children: venueMode }), venues?.venueMode === "neutral" && (_jsxs(Typography, { variant: "body2", color: "text.secondary", children: [pitchCount, " pitch", pitchCount !== 1 ? "es" : "", " selected"] }))] }), _jsxs(Paper, { variant: "outlined", sx: { p: 2, flex: 1 }, children: [_jsxs(Stack, { direction: "row", spacing: 1.5, alignItems: "center", sx: { mb: 1 }, children: [_jsx(ScheduleIcon, { color: "primary", fontSize: "small" }), _jsx(Typography, { variant: "subtitle2", fontWeight: 600, children: "Match Timing" })] }), _jsxs(Typography, { variant: "body2", color: "text.secondary", children: [matchDuration, " min matches"] }), _jsxs(Typography, { variant: "body2", color: "text.secondary", children: [totalSlotTime, " min slots total"] })] }), _jsxs(Paper, { variant: "outlined", sx: { p: 2, flex: 1 }, children: [_jsxs(Stack, { direction: "row", spacing: 1.5, alignItems: "center", sx: { mb: 1 }, children: [_jsx(EventIcon, { color: "primary", fontSize: "small" }), _jsx(Typography, { variant: "subtitle2", fontWeight: 600, children: "Schedule" })] }), _jsx(Typography, { variant: "body2", color: "text.secondary", children: startDate ? format(startDate, "MMM d, yyyy") : "Not set" }), (excludedDates.length > 0 || blackoutPeriods.length > 0) && (_jsxs(Typography, { variant: "body2", color: "text.secondary", children: [excludedDates.length + blackoutPeriods.length, " exclusion", excludedDates.length + blackoutPeriods.length !== 1
                                                ? "s"
                                                : ""] }))] })] }), _jsxs(Paper, { variant: "outlined", sx: { p: 2, mb: 3 }, children: [_jsxs(Stack, { direction: { xs: "column", sm: "row" }, spacing: 3, divider: _jsx(Divider, { orientation: "vertical", flexItem: true }), justifyContent: "center", children: [_jsxs(Box, { sx: { textAlign: "center", minWidth: 80 }, children: [_jsx(Typography, { variant: "h4", fontWeight: 700, color: "primary", children: gameWeeks.length + playoffWeeks.length }), _jsx(Typography, { variant: "caption", color: "text.secondary", children: "total weeks" })] }), _jsxs(Box, { sx: { textAlign: "center", minWidth: 80 }, children: [_jsx(Typography, { variant: "h4", fontWeight: 700, color: "primary", children: totalGames + totalPlayoffMatches }), _jsx(Typography, { variant: "caption", color: "text.secondary", children: "total matches" })] }), _jsxs(Box, { sx: { textAlign: "center", minWidth: 120 }, children: [_jsxs(Typography, { variant: "h6", fontWeight: 600, color: "text.primary", children: [startDate ? format(startDate, "MMM d") : "—", " → ", estimatedEndDate
                                                        ? format(estimatedEndDate, "MMM d, yyyy")
                                                        : "—"] }), _jsx(Typography, { variant: "caption", color: "text.secondary", children: "season span" })] })] }), _jsxs(Stack, { direction: "row", spacing: 3, sx: { mt: 2, pt: 2, borderTop: 1, borderColor: "divider" }, divider: _jsx(Divider, { orientation: "vertical", flexItem: true }), justifyContent: "center", children: [_jsxs(Box, { children: [_jsx(Typography, { variant: "body2", fontWeight: 500, children: "Regular Season" }), _jsxs(Typography, { variant: "caption", color: "text.secondary", children: [gameWeeks.length, " weeks \u2022 ", totalGames, " matches"] })] }), playoffWeeks.length > 0 && (_jsxs(Box, { children: [_jsx(Typography, { variant: "body2", fontWeight: 500, sx: { color: "#D4AF37" }, children: "Playoffs" }), _jsxs(Typography, { variant: "caption", color: "text.secondary", children: [playoffWeeks.length, " weeks \u2022 ", totalPlayoffMatches, " matches"] })] }))] })] }), _jsxs(Paper, { variant: "outlined", sx: { p: 2, mb: 3 }, children: [_jsxs(Stack, { direction: "row", alignItems: "center", justifyContent: "space-between", sx: { mb: 2 }, children: [_jsx(IconButton, { onClick: goToPrevMonth, size: "small", children: _jsx(NavigateBeforeIcon, {}) }), _jsx(Typography, { variant: "h6", fontWeight: 600, children: format(currentMonth, "MMMM yyyy") }), _jsx(IconButton, { onClick: goToNextMonth, size: "small", children: _jsx(NavigateNextIcon, {}) })] }), _jsx(Box, { sx: {
                                    display: "grid",
                                    gridTemplateColumns: "repeat(7, 1fr)",
                                    gap: 0.5,
                                    mb: 1,
                                }, children: getDayNames(gameWeekStartDay).map((day) => (_jsx(Typography, { variant: "caption", fontWeight: 600, color: "text.secondary", sx: { textAlign: "center", py: 0.5 }, children: day }, day))) }), _jsx(Box, { sx: {
                                    display: "grid",
                                    gridTemplateColumns: "repeat(7, 1fr)",
                                    gap: 0.5,
                                }, children: calendarDays.map((day, index) => {
                                    const isCurrentMonth = isSameMonth(day, currentMonth);
                                    const dayOfWeek = getDay(day);
                                    const isPlayDay = enabledDays.has(dayOfWeek);
                                    const exclusion = isDateExcluded(day);
                                    const wholeWeekExclusion = isInWholeWeekExclusion(day);
                                    const blackout = isInBlackout(day);
                                    const gameWeek = getGameWeekForDate(day);
                                    const playoffWeek = getPlayoffWeekForDate(day);
                                    const isExcluded = exclusion || wholeWeekExclusion || blackout;
                                    const isToday = isSameDay(day, new Date());
                                    const showGameWeekBar = gameWeek && isPlayDay && !isExcluded;
                                    const showPlayoffBar = playoffWeek && isPlayDay && !isExcluded && !gameWeek;
                                    const maxPlayoffRounds = playoffWeeks.length;
                                    let tooltipContent = "";
                                    if (exclusion && !exclusion.wholeWeek) {
                                        tooltipContent = `Excluded: ${exclusion.reason}`;
                                    }
                                    else if (wholeWeekExclusion) {
                                        tooltipContent = `Week excluded: ${wholeWeekExclusion.reason}`;
                                    }
                                    else if (blackout) {
                                        tooltipContent = `Blackout: ${blackout.reason}`;
                                    }
                                    else if (playoffWeek) {
                                        tooltipContent = `Playoffs: ${getPlayoffRoundName(playoffWeek.roundNumber, maxPlayoffRounds)}`;
                                    }
                                    else if (gameWeek) {
                                        tooltipContent = `Gameweek ${gameWeek.weekNumber}`;
                                    }
                                    return (_jsx(Tooltip, { title: tooltipContent, arrow: true, children: _jsxs(Box, { sx: {
                                                aspectRatio: "1",
                                                display: "flex",
                                                flexDirection: "column",
                                                borderRadius: 1,
                                                border: 1,
                                                borderColor: isToday ? "secondary.main" : "divider",
                                                borderWidth: isToday ? 2 : 1,
                                                position: "relative",
                                                cursor: tooltipContent ? "help" : "default",
                                                overflow: "hidden",
                                            }, children: [_jsxs(Box, { sx: { minHeight: 24, position: "relative" }, children: [showGameWeekBar && (_jsx(Box, { sx: {
                                                                position: "absolute",
                                                                top: 2,
                                                                left: 2,
                                                                right: 2,
                                                                height: 20,
                                                                bgcolor: "primary.main",
                                                                borderRadius: 0.5,
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                            }, children: _jsxs(Typography, { variant: "caption", sx: {
                                                                    color: "white",
                                                                    fontWeight: 700,
                                                                    fontSize: "0.7rem",
                                                                    lineHeight: 1,
                                                                    whiteSpace: "nowrap",
                                                                }, children: ["GW", gameWeek.weekNumber] }) })), showPlayoffBar && (_jsx(Box, { sx: {
                                                                position: "absolute",
                                                                top: 2,
                                                                left: 2,
                                                                right: 2,
                                                                height: 20,
                                                                bgcolor: "#D4AF37",
                                                                borderRadius: 0.5,
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                            }, children: _jsx(Typography, { variant: "caption", sx: {
                                                                    color: "white",
                                                                    fontWeight: 700,
                                                                    fontSize: "0.65rem",
                                                                    lineHeight: 1,
                                                                    whiteSpace: "nowrap",
                                                                }, children: getPlayoffRoundName(playoffWeek.roundNumber, maxPlayoffRounds) }) })), isExcluded && (_jsx(Box, { sx: {
                                                                position: "absolute",
                                                                top: 2,
                                                                left: 2,
                                                                right: 2,
                                                                height: 20,
                                                                bgcolor: "error.main",
                                                                borderRadius: 0.5,
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                            }, children: _jsx(Typography, { variant: "caption", sx: {
                                                                    color: "white",
                                                                    fontWeight: 600,
                                                                    fontSize: "0.5rem",
                                                                    lineHeight: 1,
                                                                    whiteSpace: "nowrap",
                                                                    overflow: "hidden",
                                                                    textOverflow: "ellipsis",
                                                                }, children: "Off" }) }))] }), _jsx(Box, { sx: {
                                                        flex: 1,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        pb: 0.5,
                                                    }, children: _jsx(Typography, { variant: "body2", fontWeight: isToday ? 700 : 400, color: isCurrentMonth ? "text.primary" : "text.disabled", children: format(day, "d") }) })] }) }, index));
                                }) }), _jsxs(Stack, { direction: "row", spacing: 2, sx: { mt: 2, pt: 2, borderTop: 1, borderColor: "divider" }, flexWrap: "wrap", children: [_jsxs(Stack, { direction: "row", spacing: 0.5, alignItems: "center", children: [_jsx(Box, { sx: {
                                                    width: 24,
                                                    height: 12,
                                                    bgcolor: "primary.main",
                                                    borderRadius: 0.5,
                                                } }), _jsx(Typography, { variant: "caption", color: "text.secondary", children: "Gameweek" })] }), playoffWeeks.length > 0 && (_jsxs(Stack, { direction: "row", spacing: 0.5, alignItems: "center", children: [_jsx(Box, { sx: {
                                                    width: 24,
                                                    height: 12,
                                                    bgcolor: "#D4AF37",
                                                    borderRadius: 0.5,
                                                } }), _jsx(Typography, { variant: "caption", color: "text.secondary", children: "Playoffs" })] })), _jsxs(Stack, { direction: "row", spacing: 0.5, alignItems: "center", children: [_jsx(Box, { sx: {
                                                    width: 24,
                                                    height: 12,
                                                    bgcolor: "error.main",
                                                    borderRadius: 0.5,
                                                } }), _jsx(Typography, { variant: "caption", color: "text.secondary", children: "Excluded" })] }), _jsxs(Stack, { direction: "row", spacing: 0.5, alignItems: "center", children: [_jsx(Box, { sx: {
                                                    width: 16,
                                                    height: 16,
                                                    border: 2,
                                                    borderColor: "secondary.main",
                                                    borderRadius: 0.5,
                                                } }), _jsx(Typography, { variant: "caption", color: "text.secondary", children: "Today" })] })] })] }), _jsxs(Paper, { variant: "outlined", sx: { p: 2, mb: 3 }, children: [_jsx(Typography, { variant: "subtitle2", fontWeight: 600, sx: { mb: 2 }, children: "Gameweek Schedule" }), _jsxs(Box, { sx: {
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 1,
                                }, children: [gameWeeks.slice(0, 20).map((gw) => (_jsx(Chip, { label: `GW${gw.weekNumber}: ${format(gw.weekStart, "MMM d")}`, size: "small", color: "primary", variant: "outlined", onClick: () => setCurrentMonth(gw.weekStart) }, gw.weekNumber))), gameWeeks.length > 20 && (_jsx(Chip, { label: `+${gameWeeks.length - 20} more`, size: "small", variant: "outlined" }))] })] }), (excludedDates.length > 0 || blackoutPeriods.length > 0) && (_jsxs(Paper, { variant: "outlined", sx: { p: 2, mb: 3, bgcolor: "error.lighter" }, children: [_jsxs(Stack, { direction: "row", spacing: 1, alignItems: "center", sx: { mb: 1 }, children: [_jsx(EventBusyIcon, { color: "error", fontSize: "small" }), _jsx(Typography, { variant: "subtitle2", fontWeight: 600, color: "error.main", children: "Exclusions Applied" })] }), _jsxs(Stack, { direction: "row", spacing: 1, sx: { flexWrap: "wrap", gap: 1 }, children: [excludedDates.map((ed, i) => (_jsx(Chip, { size: "small", label: ed.wholeWeek
                                            ? `Week of ${format(ed.date, "MMM d")} (${ed.reason})`
                                            : `${format(ed.date, "MMM d")} (${ed.reason})`, color: "error", variant: "outlined" }, i))), blackoutPeriods.map((bp, i) => (_jsx(Chip, { size: "small", label: `${format(bp.startDate, "MMM d")}–${format(bp.endDate, "MMM d")} (${bp.reason})`, color: "error", variant: "outlined" }, `bp-${i}`)))] })] }))] })), generateError && (_jsx(Alert, { severity: "error", sx: { mb: 3 }, children: generateError })), !isReady && (_jsxs(Alert, { severity: "warning", sx: { mb: 3 }, children: [_jsx(Typography, { variant: "body2", fontWeight: 500, gutterBottom: true, children: scheduleMode === "manual"
                            ? "Cannot activate season yet:"
                            : "Cannot generate fixtures yet:" }), _jsxs(Stack, { component: "ul", sx: { m: 0, pl: 2 }, spacing: 0.5, children: [teamCount < 2 && (_jsx(Typography, { component: "li", variant: "body2", children: "Need at least 2 teams assigned to divisions" })), scheduleMode !== "manual" && !formatRounds?.matchesPerPairing && (_jsx(Typography, { component: "li", variant: "body2", children: "Match format not configured" })), scheduleMode !== "manual" && !matchTiming?.matchDuration && (_jsx(Typography, { component: "li", variant: "body2", children: "Match timing not configured" })), scheduleMode !== "manual" && !formatRounds?.startDate && (_jsx(Typography, { component: "li", variant: "body2", children: "Season start date not set" })), scheduleMode !== "manual" && !hasSchedule && (_jsx(Typography, { component: "li", variant: "body2", children: "No play days configured in schedule" })), scheduleMode !== "manual" &&
                                gameWeeks.length === 0 &&
                                hasSchedule && (_jsx(Typography, { component: "li", variant: "body2", children: "Unable to calculate gameweeks" }))] })] })), _jsx(Box, { sx: { display: "flex", justifyContent: "center" }, children: _jsx(Button, { variant: "contained", size: "large", color: "success", disabled: !isReady || isGenerating, onClick: onGenerate, startIcon: isGenerating ? (_jsx(CircularProgress, { size: 20, color: "inherit" })) : (_jsx(CheckCircleIcon, {})), sx: { px: 6, py: 1.5, fontSize: "1.1rem" }, children: isGenerating
                        ? scheduleMode === "manual"
                            ? "Activating..."
                            : "Generating..."
                        : scheduleMode === "manual"
                            ? "Activate Season"
                            : "Generate Fixtures" }) }), _jsx(Typography, { variant: "caption", color: "text.secondary", sx: { display: "block", textAlign: "center", mt: 2 }, children: scheduleMode === "manual" ? (_jsxs(_Fragment, { children: ["This will activate the season with no matches.", _jsx("br", {}), "You can create matches individually from the season page."] })) : (_jsxs(_Fragment, { children: ["This will create all ", totalGames + totalPlayoffMatches, " matches for the season.", _jsx("br", {}), "You can edit individual matches after generation."] })) })] }));
};
